import React, { useEffect, useRef, useCallback } from "react";
import axios from "../../../api/axios";
import Axios from "axios";

export const UploaderV4 = ({
  onUploadStart,
  filesToUpload,
  onUploadComplete,
  onProgressChange,
  onFileUploadComplete,
  onReset,
}) => {
  const uploadFunctionRef = useRef(null);
  const hasNotifiedRef = useRef(false);
  const previousFilesLengthRef = useRef(0);

  const startUpload = useCallback(async () => {
    if (!filesToUpload || filesToUpload.length === 0) {
      return;
    }

    const uploadedFiles = [];

    for (const { file, title, module, moduleParam, folder_id, drawing_id, drawing_position_x, drawing_position_y, drawing_rotation } of filesToUpload) {
      try {
        // Get presigned URL
        const response = await axios.get("/uploads/presigned_url", {
          params: {
            filename: file.name,
            module,
            module_param: moduleParam,
            title,
            folder_id: folder_id || null,
          },
        });

        const { url, document_id } = response.data;
        if (!url || !document_id) {
          throw new Error("Invalid presigned URL response");
        }

        const isDevUpload = url.includes("/uploads/dev_upload/");
        const uploadOptions = {
          headers: { "Content-Type": file.type || "application/octet-stream" },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );

              if (onProgressChange) {
                onProgressChange({
                  [document_id]: percentCompleted,
                });
              }
            }
          },
        };

        if (isDevUpload) {
          await axios.put(`/uploads/dev_upload/${document_id}`, file, uploadOptions);
        } else {
          await Axios.put(url, file, uploadOptions);
        }

        // Complete upload
        const fileResponse = await axios.patch("/uploads/complete_upload", {
          id: document_id,
          filename: file.name,
          title,
          module,
          module_param: moduleParam,
          folder_id: folder_id || null,
          drawing_id: drawing_id || null,
          drawing_position_x: drawing_position_x || null,
          drawing_position_y: drawing_position_y || null,
          drawing_rotation: drawing_rotation || 0,
        });

        uploadedFiles.push(fileResponse.data.document);

        if (onFileUploadComplete) {
          onFileUploadComplete();
        }
      } catch (error) {
        console.error("Upload failed for file:", file.name, error);
        throw error;
      }
    }

    if (onUploadComplete) {
      onUploadComplete(uploadedFiles);
    }
  }, [filesToUpload, onProgressChange, onFileUploadComplete, onUploadComplete]);

  // Always keep ref in sync (e.g. user edits titles — same file count, new `startUpload` closure).
  useEffect(() => {
    uploadFunctionRef.current = startUpload;
  }, [startUpload]);

  // Notify parent when staged files appear (only when length > 0).
  useEffect(() => {
    const currentFilesLength = filesToUpload?.length || 0;

    if (currentFilesLength === 0) {
      hasNotifiedRef.current = false;
      previousFilesLengthRef.current = 0;
      return;
    }

    const filesChanged = currentFilesLength !== previousFilesLengthRef.current;

    if (onUploadStart && (filesChanged || !hasNotifiedRef.current)) {
      hasNotifiedRef.current = true;
      previousFilesLengthRef.current = currentFilesLength;

      onUploadStart(() => {
        return uploadFunctionRef.current();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesToUpload?.length, onUploadStart]);

  // Reset function
  const resetUploadProgress = useCallback(() => {
    hasNotifiedRef.current = false;
    previousFilesLengthRef.current = 0;
  }, []);

  // Provide reset function to parent
  useEffect(() => {
    if (onReset) {
      onReset(resetUploadProgress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReset]);

  return null;
};
